import React from 'react';
import type { OpportunityLoss as OL } from '../types';

const formatYen = (n: number): string => {
  if (n >= 100_000) return `¥${(n / 10_000).toFixed(0)}万`;
  return `¥${n.toLocaleString()}`;
};

const OpportunityLoss: React.FC<{ data: OL }> = ({ data }) => (
  <section className="py-4 border-b border-slate-200">
    <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
      <h3 className="text-[13px] font-semibold text-slate-900">月間機会損失</h3>
      <span className="text-[11px] text-slate-500">
        現状 {data.current_rank}位 → 目標 {data.target_rank}位
      </span>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 mb-4 pb-4 border-b border-slate-100">
      <div>
        <div className="text-[11px] text-slate-500 mb-1">毎月の機会損失</div>
        <div className="text-2xl font-semibold text-rose-700 leading-none tabular-nums">
          {formatYen(data.headline_yen_monthly)}
        </div>
      </div>
      <div>
        <div className="text-[11px] text-slate-500 mb-1">年間換算</div>
        <div className="text-2xl font-semibold text-slate-900 leading-none tabular-nums">
          {formatYen(data.headline_yen_annual)}
        </div>
      </div>
      <div>
        <div className="text-[11px] text-slate-500 mb-1">逃している月間問い合わせ</div>
        <div className="text-2xl font-semibold text-slate-900 leading-none tabular-nums">
          {data.lost_inquiries_monthly.toFixed(1)}<span className="text-sm font-normal text-slate-500"> 件</span>
        </div>
      </div>
    </div>

    <h4 className="text-[11px] font-semibold text-slate-700 mb-2">機会損失の構造</h4>
    <table className="w-full text-[12px]">
      <tbody>
        <tr className="border-b border-slate-100">
          <td className="py-1.5 text-slate-600">「{data.current_rank > 0 ? '' : ''}」検索の月間ボリューム</td>
          <td className="py-1.5 text-right tabular-nums font-semibold">{data.search_volume_monthly.toLocaleString()} 回</td>
        </tr>
        <tr className="border-b border-slate-100">
          <td className="py-1.5 text-slate-600">現在のCTR（{data.current_rank}位）→ 月間クリック数</td>
          <td className="py-1.5 text-right tabular-nums">{data.current_ctr_pct}% → {data.current_clicks_monthly.toLocaleString()} クリック</td>
        </tr>
        <tr className="border-b border-slate-100">
          <td className="py-1.5 text-slate-600">目標のCTR（{data.target_rank}位）→ 月間クリック数</td>
          <td className="py-1.5 text-right tabular-nums text-leaf-700 font-semibold">{data.target_ctr_pct}% → {data.target_clicks_monthly.toLocaleString()} クリック</td>
        </tr>
        <tr className="border-b border-slate-100">
          <td className="py-1.5 text-slate-600">逃しているクリック × CVR {data.assumptions.cvr_pct}%</td>
          <td className="py-1.5 text-right tabular-nums">{data.lost_clicks_monthly.toLocaleString()} → 月 {data.lost_inquiries_monthly.toFixed(1)} 件問合せ</td>
        </tr>
        <tr className="border-b border-slate-100">
          <td className="py-1.5 text-slate-600">成約率 {data.assumptions.closing_rate_pct}% → 月間入会</td>
          <td className="py-1.5 text-right tabular-nums">月 {data.lost_contracts_monthly.toFixed(1)} 件</td>
        </tr>
        <tr>
          <td className="py-1.5 text-slate-600">客単価 ¥{data.assumptions.monthly_unit_price_yen.toLocaleString()}/月</td>
          <td className="py-1.5 text-right tabular-nums text-rose-700 font-semibold">{formatYen(data.lost_revenue_monthly_yen)} / 月</td>
        </tr>
      </tbody>
    </table>
    <div className="text-[10px] text-slate-400 mt-2 leading-relaxed">
      業界平均値ベースの試算。実際の値は店舗のCVR・成約率・客単価によって変動。
      推定値（{data.assumptions.search_volume_source}）。
    </div>
  </section>
);

export default OpportunityLoss;
